import { useState } from 'react';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import {
  Star, Search, SlidersHorizontal, CheckCircle,
  ThumbsUp, MessageSquare, Award, TrendingUp, X,
} from 'lucide-react';
import { useOrders } from '../../hooks/useOrders';
import { useReviews } from '../../hooks/useReviews';
import { usePageTitle } from '../../hooks/usePageTitle';

interface ReviewsPageProps {
  onNavigate?: (page: string) => void;
}

function StarRow({
  rating,
  size = 'md',
  color = 'yellow',
}: {
  rating: number;
  size?: 'sm' | 'md' | 'lg';
  color?: 'yellow' | 'orange' | 'white';
}) {
  const sz = size === 'sm' ? 'w-3 h-3' : size === 'lg' ? 'w-5 h-5' : 'w-4 h-4';
  const filled =
    color === 'orange'
      ? 'fill-orange-400 text-orange-400'
      : color === 'white'
      ? 'fill-white text-white'
      : 'fill-yellow-400 text-yellow-400';
  const empty =
    color === 'orange'
      ? 'text-orange-900/40'
      : color === 'white'
      ? 'text-white/30'
      : 'text-gray-300 dark:text-gray-600';

  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star key={s} className={`${sz} ${s <= rating ? filled : empty}`} />
      ))}
    </div>
  );
}

function Avatar({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' | 'lg' }) {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const sz =
    size === 'sm' ? 'w-8 h-8 text-xs' : size === 'lg' ? 'w-14 h-14 text-base' : 'w-11 h-11 text-sm';
  return (
    <div
      className={`${sz} rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center font-bold text-white flex-shrink-0`}
    >
      {initials}
    </div>
  );
}

export function ReviewsPage({ onNavigate }: ReviewsPageProps) {
  usePageTitle('Reviews');

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') return window.innerWidth >= 768;
    return true;
  });
  const [filterRating, setFilterRating] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  const { reviews: apiReviews, loading: reviewsLoading, verifiedReviews } = useReviews();
  const { orders: ordersData } = useOrders({ page: 1, limit: 50 });

  const reviews = apiReviews.map((review) => ({
    id: review.id,
    customerName: review.customerName || 'Anonymous',
    rating: review.rating,
    comment: review.comment || '',
    date: new Date(review.createdAt).toLocaleDateString(),
    rawDate: new Date(review.createdAt),
    dish: review.productName || null,
    verified: review.isVerified,   // ← was isVerifiedPurchase
  }));

  // Stats
  const totalReviews = reviews.length;
  const averageRating =
    totalReviews > 0
      ? (reviews.reduce((s, r) => s + r.rating, 0) / totalReviews).toFixed(1)
      : '0.0';
  // verifiedReviews comes from /reviews/stats via the hook (accurate server count)
  const fiveStarReviews = reviews.filter((r) => r.rating === 5).length;

  const ratingDistribution: Record<number, number> = {
    5: reviews.filter((r) => r.rating === 5).length,
    4: reviews.filter((r) => r.rating === 4).length,
    3: reviews.filter((r) => r.rating === 3).length,
    2: reviews.filter((r) => r.rating === 2).length,
    1: reviews.filter((r) => r.rating === 1).length,
  };

  // Sort newest first
  const sorted = [...reviews].sort(
    (a, b) => b.rawDate.getTime() - a.rawDate.getTime(),
  );

  // Filter
  const filtered = sorted.filter((r) => {
    const matchRating = filterRating === null || r.rating === filterRating;
    const q = searchQuery.toLowerCase();
    const matchSearch =
      !q ||
      r.customerName.toLowerCase().includes(q) ||
      r.comment.toLowerCase().includes(q) ||
      (r.dish ?? '').toLowerCase().includes(q);
    return matchRating && matchSearch;
  });

  const featuredReview = filtered[0] ?? null;
  const restReviews = filtered.slice(1);

  const statCards = [
    {
      label: 'Average Rating',
      value: `${averageRating} / 5`,
      icon: Star,
      iconClass: 'text-indigo-600 dark:text-indigo-400 fill-indigo-600 dark:fill-indigo-400',
      bg: 'bg-indigo-50 dark:bg-indigo-900/30',
    },
    {
      label: 'Total Reviews',
      value: totalReviews,
      icon: MessageSquare,
      iconClass: 'text-green-600 dark:text-green-400',
      bg: 'bg-green-50 dark:bg-green-900/30',
    },
    {
      label: 'Verified Reviews',
      value: verifiedReviews,
      icon: CheckCircle,
      iconClass: 'text-purple-600 dark:text-purple-400',
      bg: 'bg-purple-50 dark:bg-purple-900/30',
    },
    {
      label: '5-Star Reviews',
      value: fiveStarReviews,
      icon: Award,
      iconClass: 'text-yellow-600 dark:text-yellow-400',
      bg: 'bg-yellow-50 dark:bg-yellow-900/30',
    },
  ];

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(!sidebarOpen)}
        activePage="reviews"
        onNavigate={onNavigate}
        ordersCount={ordersData.length}
      />

      <div
        className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
          sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'
        }`}
      >
        <TopBar
          onMenuClick={() => setSidebarOpen(!sidebarOpen)}
          sidebarOpen={sidebarOpen}
          onNavigate={onNavigate}
        />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1400px] mx-auto">

            {/* Header */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Customer Reviews</h1>
              <p className="text-gray-400 dark:text-gray-500 mt-1 text-sm">
                Customer feedback and ratings
              </p>
            </div>

            {/* Stat Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {statCards.map((card) => (
                <div
                  key={card.label}
                  className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-4 flex items-center gap-4"
                >
                  <div className={`p-3 rounded-xl flex-shrink-0 ${card.bg}`}>
                    <card.icon className={`w-5 h-5 ${card.iconClass}`} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{card.label}</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{card.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Rating Distribution */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">Rating Distribution</h2>
                <div className="flex items-center gap-1.5">
                  <TrendingUp className="w-4 h-4 text-green-500" />
                  <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                    {averageRating} avg
                  </span>
                </div>
              </div>
              <div className="space-y-2.5">
                {[5, 4, 3, 2, 1].map((r) => {
                  const count = ratingDistribution[r];
                  const pct = totalReviews > 0 ? Math.round((count / totalReviews) * 100) : 0;
                  const barColor =
                    r >= 4
                      ? 'bg-green-400'
                      : r === 3
                      ? 'bg-yellow-400'
                      : 'bg-red-400';
                  return (
                    <div key={r} className="flex items-center gap-3">
                      <div className="flex items-center gap-1 w-14 flex-shrink-0">
                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{r}</span>
                        <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                      </div>
                      <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 w-20 text-right flex-shrink-0">
                        {count} ({pct}%)
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Search & Filter */}
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  placeholder="Search by customer, dish, or comment…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                />
              </div>

              <div className="relative">
                <button
                  onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <SlidersHorizontal className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">Filter</span>
                  {filterRating !== null && (
                    <span className="ml-1 px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-xs font-medium rounded-full">
                      {filterRating}★
                    </span>
                  )}
                </button>

                {showFilterDropdown && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowFilterDropdown(false)} />
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-20 p-2">
                      <div className="flex items-center justify-between px-3 py-2">
                        <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">Filter by Rating</p>
                        <button onClick={() => setShowFilterDropdown(false)}>
                          <X className="w-3.5 h-3.5 text-gray-400" />
                        </button>
                      </div>
                      <button
                        onClick={() => { setFilterRating(null); setShowFilterDropdown(false); }}
                        className={`w-full text-left px-3 py-2 text-xs rounded-lg transition-colors ${
                          filterRating === null
                            ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-medium'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        All Ratings
                      </button>
                      {[5, 4, 3, 2, 1].map((r) => (
                        <button
                          key={r}
                          onClick={() => { setFilterRating(r); setShowFilterDropdown(false); }}
                          className={`w-full text-left px-3 py-2 text-xs rounded-lg flex items-center gap-2 transition-colors ${
                            filterRating === r
                              ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-medium'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          <span>{r}</span>
                          <StarRow rating={r} size="sm" />
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {filterRating !== null && (
                <button
                  onClick={() => setFilterRating(null)}
                  className="flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  <X className="w-3.5 h-3.5" /> Clear
                </button>
              )}
            </div>

            {/* Loading */}
            {reviewsLoading && (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">Loading reviews…</p>
                </div>
              </div>
            )}

            {/* Empty */}
            {!reviewsLoading && filtered.length === 0 && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">No Reviews Found</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Try adjusting your filters or search query
                </p>
              </div>
            )}

            {/* ── FEATURED / LATEST REVIEW ── */}
            {!reviewsLoading && featuredReview && (
              <div className="mb-6">
                {/* Label */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-semibold text-orange-500 uppercase tracking-wider">Latest Review</span>
                  <div className="flex-1 h-px bg-orange-200 dark:bg-orange-900/40" />
                </div>

                <div className="relative rounded-2xl overflow-hidden shadow-lg bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border border-gray-700">
                  {/* Decorative blobs */}
                  <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full -translate-y-1/2 translate-x-1/4 pointer-events-none" />
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-indigo-500/10 rounded-full translate-y-1/2 -translate-x-1/4 pointer-events-none" />

                  <div className="relative p-6 md:p-8">
                    <div className="flex flex-col md:flex-row md:items-start gap-6">

                      {/* Left — avatar + meta */}
                      <div className="flex items-start gap-4 flex-shrink-0">
                        <div className="relative">
                          <Avatar name={featuredReview.customerName} size="lg" />
                          {featuredReview.verified && (
                            <span className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center border-2 border-gray-800">
                              <CheckCircle className="w-3 h-3 text-white fill-white" />
                            </span>
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-base font-bold text-white">{featuredReview.customerName}</h3>
                            <span className="flex items-center gap-1 px-2.5 py-0.5 bg-orange-500 rounded-full">
                              <span className="text-sm">🔥</span>
                              <span className="text-xs font-semibold text-white">Newest</span>
                            </span>
                          </div>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {featuredReview.rawDate.toLocaleDateString('en-US', {
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </p>
                          {/* Stars */}
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-2xl font-bold text-orange-400">{featuredReview.rating.toFixed(1)}</span>
                            <StarRow rating={featuredReview.rating} size="md" color="orange" />
                          </div>
                        </div>
                      </div>

                      {/* Right — comment + dish */}
                      <div className="flex-1 md:border-l md:border-gray-700 md:pl-6">
                        {featuredReview.dish && (
                          <span className="inline-block px-3 py-1 mb-3 text-xs font-medium rounded-full bg-gray-700 text-gray-300">
                            🍽️ {featuredReview.dish}
                          </span>
                        )}
                        <p className="text-gray-200 leading-relaxed text-sm">
                          {featuredReview.comment || 'No comment provided.'}
                        </p>

                        {/* Bottom row */}
                        <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-700/60">
                          <div className="flex items-center gap-2 text-xs text-gray-400">
                            <ThumbsUp className="w-3.5 h-3.5" />
                            <span>Verified Purchase</span>
                          </div>
                          <span className="text-xs font-mono text-gray-600">
                            #{featuredReview.id.slice(0, 8)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── REST OF REVIEWS GRID ── */}
            {!reviewsLoading && restReviews.length > 0 && (
              <>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    All Reviews
                  </span>
                  <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                  <span className="text-xs text-gray-400 dark:text-gray-500">{restReviews.length} reviews</span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {restReviews.map((review) => (
                    <div
                      key={review.id}
                      className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-5 hover:shadow-md transition-shadow"
                    >
                      {/* Card Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <Avatar name={review.customerName} size="md" />
                            {review.verified && (
                              <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center border-2 border-white dark:border-gray-900">
                                <CheckCircle className="w-2.5 h-2.5 text-white fill-white" />
                              </span>
                            )}
                          </div>
                          <div>
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                              {review.customerName}
                            </h3>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                              {review.rawDate.toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </p>
                          </div>
                        </div>

                        {/* Rating badge */}
                        <div className="flex flex-col items-end gap-1">
                          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                            <span className="text-sm font-bold text-yellow-700 dark:text-yellow-400">
                              {review.rating.toFixed(1)}
                            </span>
                            <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                          </div>
                          <StarRow rating={review.rating} size="sm" />
                        </div>
                      </div>

                      {/* Dish tag */}
                      {review.dish && (
                        <span className="inline-block px-2.5 py-0.5 mb-2.5 text-xs font-medium rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400">
                          🍽️ {review.dish}
                        </span>
                      )}

                      {/* Comment */}
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-3">
                        {review.comment || 'No comment provided.'}
                      </p>

                      {/* Footer */}
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 dark:border-gray-800">
                        {review.verified ? (
                          <span className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
                            <CheckCircle className="w-3.5 h-3.5" />
                            Verified
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500">Unverified</span>
                        )}
                        <span className="text-xs font-mono text-gray-400 dark:text-gray-500">
                          #{review.id.slice(0, 8)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

          </div>
        </main>
      </div>
    </div>
  );
}